import {Injectable} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {DescriptorConfig} from "./descriptors/descriptor-config.model";
import {ConfigStructure} from "./config-structure.model";
import {AppConfig} from "./app-config";
import {DescriptorEnum} from "./descriptors/descriptor.enum";
import {WorkConfigEnum} from "./work/work-config.enum";
import {MaterializationEnum} from "./materialization/materialization-config.enum";
import {WorkMaterializationEnum} from "./work_materialization/materialization-config.enum";
import {WorkConfig} from "./work/work-config.model";
import {MaterializationConfig} from "./materialization/materialization-config.model";
import {WorkMaterializationConfig} from "./work_materialization/materialization-config.model";
import {SuggestionsConfig} from "./suggestions/suggestions-config.model";
import {WorkDetailsConfig} from "./work/work-config-details.model";
import {MuseumObjectDetailsConfig} from "./museum_object/museum-object-details-config";
import {MuseumObjectConfig} from "./museum_object/museum-object-config";
import {FiltersConfig} from "./filters/filters-config.model";
import {FieldToSearch} from "../search/search-list/fields-to-search.model";
import {FulltextConfig} from "./fulltext/fulltext-config";
import {HtmlUtilsService} from "../html-utils/html-utils.service";
import {FeedbackComponent} from "../footer/feedback/feedback.component";
import {AuthService} from '../auth/auth.service';
import {IndexDTO} from "./index/index-dto";
import {Browser} from "../html-utils/detect-browser/browser";
import {TabConfigStructure} from "./tab-config-structure";
import {forkJoin} from 'rxjs';
import {UserCollectionsService} from '../auth/account/user-collections/user-collections.service';
import {EuModel} from "./eu/eu.model";
import {ExternalAppsDto} from '../external-apps/external-apps/external-apps-dto.model';
import {ExternalAppDto} from '../external-apps/external-apps/external-app-dto.model';
import {DescriptorsFacetConfig} from "./filters/descriptors-facet-config.model";
import {DescriptorFacetConfig} from './filters/descriptor-facet-config.model';
import {FilterPortalConfig} from './filters/filter-portal-config.model';
import {PublicDatasetConfig} from "./public-dataset/public-dataset-config.model";
import * as moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class AppConfigService {

  configDirectory = 'assets/config/';
  annalyticsConfig;
  isbnDescriptionConfig;
  descriptorConfig = new DescriptorConfig();
  workConfig = new WorkConfig();
  publicDatasetConfig = new PublicDatasetConfig();
  materializationConfig = new MaterializationConfig();
  workMaterializationConfig = new WorkMaterializationConfig();
  fulltextConfig = new FulltextConfig();
  suggestionsConfig: SuggestionsConfig;
  filtersConfig: FiltersConfig;
  rawFiltersConfig;
  workDetailsConfig = new WorkDetailsConfig();
  museumObjectDetailsConfig = new MuseumObjectDetailsConfig();
  museumObjectConfig = new MuseumObjectConfig();
  private appConfig: AppConfig = new AppConfig();
  librariesPerPage: number;
  institutionsLibrariesPerPage: number;
  materializationListConfig: ConfigStructure[];
  materializationPerPage: number;
  fieldsToSearch: FieldToSearch[] = [];
  zoomConfig: number = 10;
  breadCrumbLength: number = 6;
  maxResultWindow: number = 10000;
  facetTagMax: number = 5;
  languages: { name: string, active: boolean }[] = [];
  showMoreConfig: { maxText: number, maxListElements: number, maxAuthors: number } = {
    maxText: 20,
    maxListElements: 3,
    maxAuthors: 1
  };
  accountConfig: { timeout: number } = {timeout: 3000};
  modalClasses: Map<string, any>;
  descriptorFacetConfig: DescriptorsFacetConfig;
  euConfig: EuModel = new EuModel();
  externalApps: ExternalAppsDto;
  mainPageConfig: { messageKey: string, active: boolean } = {
    messageKey: "MainPage.text",
    active: true
  };

  siteMapConfig;

  supportedBrowsers: Browser[] = [
    {
      browser: 'firefox',
      version: '60',
      browserUrl: 'https://www.mozilla.org/‎',
      imgUrl: 'assets/img/icons/browser/firefox_64x64.png'
    },
    {
      browser: 'chrome',
      version: '69',
      browserUrl: 'https://www.google.com/chrome/',
      imgUrl: 'assets/img/icons/browser/chrome_64x64.png'
    },
    {
      browser: 'opera',
      version: '56',
      browserUrl: 'https://www.opera.com/pl',
      imgUrl: 'assets/img/icons/browser/opera_64x64.png'
    },
    {
      browser: 'ms-edge',
      version: '17',
      browserUrl: 'https://www.microsoft.com/pl-pl/windows/microsoft-edge',
      imgUrl: 'assets/img/icons/browser/edge_64x64.png'
    },
    {
      browser: 'ie',
      version: '11',
      browserUrl: 'https://support.microsoft.com/pl-pl/help/18520/download-internet-explorer-11-offline-installer',
      imgUrl: 'assets/img/icons/browser/ie_11_64x64.png'
    },
    {
      browser: 'safari',
      version: '11',
      browserUrl: 'https://www.apple.com/safari/',
      imgUrl: 'assets/img/icons/browser/safari_64x64.png'
    },
  ];

  refreshConfig = true;

  constructor(private http: HttpClient,
              private htmlUtils: HtmlUtilsService,
              private authService: AuthService,
              private userCollectionsService: UserCollectionsService) {
    //register components to dynamicaly load from string
    this.modalClasses = new Map();
    this.modalClasses.set('FeedbackComponent', FeedbackComponent);
  }

  loadConfig(): Promise<any> {
    if (localStorage.getItem('configExpTime')) {
      const expTime = localStorage.getItem('configExpTime');
      this.refreshConfig = moment(new Date(), 'YYYY-MM-DD HH:mm:ss').isAfter(moment(expTime, 'YYYY-MM-DD HH:mm:ss'));
      if (this.refreshConfig) {
        const newExpTime = moment(new Date()).add(30, 'minutes').format('YYYY-MM-DD HH:mm:ss');
        localStorage.setItem('configExpTime', JSON.stringify(newExpTime));
      }
    } else {
      const newExpTime = moment(new Date()).add(5, 'minutes').format('YYYY-MM-DD HH:mm:ss');
      localStorage.setItem('configExpTime', JSON.stringify(newExpTime));
      this.refreshConfig = true;
    }

    return forkJoin([
        this.loadAppConfig(this.configDirectory + 'appConfig.json'),
        this.loadAnalyticsConfig(this.configDirectory + 'analyticsConfig.json'),
        this.loadDescriptorConfig(DescriptorEnum.PERSONAL, this.configDirectory + 'descriptors/personal-descriptor.config.json'),
        this.loadDescriptorConfig(DescriptorEnum.CORPORATE, this.configDirectory + 'descriptors/corporate-descriptor.config.json'),
        this.loadDescriptorConfig(DescriptorEnum.EVENT, this.configDirectory + 'descriptors/event-descriptor.config.json'),
        this.loadDescriptorConfig(DescriptorEnum.GEOGRAPHICAL, this.configDirectory + 'descriptors/geographical-descriptor.config.json'),
        this.loadDescriptorConfig(DescriptorEnum.SUBJECT, this.configDirectory + 'descriptors/subject-descriptor.config.json'),
        this.loadDescriptorConfig(DescriptorEnum.GENRE, this.configDirectory + 'descriptors/genre-descriptor.config.json'),
        this.loadWorkConfigFromFile(WorkConfigEnum.BOOK, this.configDirectory + 'work/work-book.config.json'),
        this.loadPublicDatasetConfigFromFile(this.configDirectory + 'public-dataset/public-dataset.config.json'),
        this.loadMaterializationFromFile(MaterializationEnum.BOOK, this.configDirectory + 'materialization/materialization-book.config.json'),
        this.loadWorkMaterializationFromFile(WorkMaterializationEnum.BOOK, this.configDirectory + 'materialization/materialization-book.config.json'),
        this.loadFulltextFromFile(this.configDirectory + 'fulltext/fulltext-book.config.json'),
        this.loadSuggestionsConfig(this.configDirectory + 'suggestionConfig.json'),
        this.loadIsbnDescriptionConfig(this.configDirectory + 'isbnDescriptionConfig.json'),
        this.loadArticlesFacetsConfig(this.configDirectory + 'work/article-facet-config.config.json'),
        this.loadTopicsFacetsConfig(this.configDirectory + 'work/topic-facet-config.config.json'),
        this.loadSiteMapConfig(this.configDirectory + 'siteMap.config.json'),
        this.loadFiltersConfig(this.configDirectory + 'filtersConfig.json'),
        this.loadWorkDetailsConfig(this.configDirectory + 'work/work-details.config.json'),
        this.loadMuseumObjectDetailsConfig(this.configDirectory + 'museum_object/museum-object-details.config.json'),
        this.loadMuseumObjectConfig(this.configDirectory + 'museum_object/museum-object.config.json'),
        this.loadLibraryPerPageConfig(this.configDirectory + 'work/library-count.config.json'),
        this.loadMaterializationListConfig(this.configDirectory + 'work/materialization-list.config.json'),
        this.loadMaterializationPerPageConfig(this.configDirectory + 'work/materialization-count.config.json'),
        this.loadFieldsToSearchConfig(this.configDirectory + 'field-to-search.config.json'),
        this.loadZoomConfig(this.configDirectory + 'maps/zoom.config.json'),
        this.loadBreadCrumbLengthConfig(this.configDirectory + 'bread-crumb/bread-crumb.config.json'),
        this.loadElasticSearchConfig(this.configDirectory + 'elasticsearch/elasticsearch.config.json'),
        this.loadFacetTagMaxConfig(this.configDirectory + 'facet-tag.config.json'),
        this.loadTranslationConfig(this.configDirectory + 'translation/translation.config.json'),
        this.loadShowMoreConfig(this.configDirectory + 'showMore.config.json'),
        this.loadAccountConfig(this.configDirectory + 'account.config.json'),
        this.loadDescriptorFacetConfig(this.configDirectory + 'descriptors/descriptor-facet-config.config.json'),
        this.loadEuConfig(this.configDirectory + 'eu/eu.config.json'),
        this.loadExternalApps(this.configDirectory + 'externalApps.json'),
        this.loadMainPageConfig(this.configDirectory + 'mainPage.config.json'),
        this.initAuth()
      ]
    ).toPromise();
  }

  getConfig() {
    return this.appConfig;
  }

  private initAuth() {
    const result = this.authService.init();
    result.then(logged => {
      if (logged) {
        this.loadCollections();
      }
    });
    return result;
  }

  private loadExternalApps(path: string) {
    if (!this.refreshConfig && localStorage.getItem('externalApps')) {
      this.externalApps = JSON.parse(localStorage.getItem('externalApps'));
      return Promise.resolve();
    } else {
      return this.http.get<ExternalAppDto[]>(path)
        .toPromise()
        .then(apps => {
          this.externalApps = new ExternalAppsDto({apps});
          localStorage.setItem('externalApps', JSON.stringify(this.externalApps));
        }).catch(reason => {
          console.log("Problem z przetwarzaniem pliku: " + path, reason);
        });
    }
  }

  private async loadCollections(): Promise<any> {
    const token = await this.authService.getToken();
    if (token) {
      try {
        await this.userCollectionsService.findList(0, 5, 'id', 'asc', token, {}).toPromise();
      } catch (e) {
        console.warn('Cannot load user colllections. Reason: ' + e);
      }
    }
  }

  private mapJSONToDescriptorConfigStructure(object: any): ConfigStructure[] {
    let collection: ConfigStructure[] = [];
    this.attachToCollection(object, collection);
    return collection;
  }

  private mapJSONToTabsConfigStructure(object: any): TabConfigStructure[] {
    let collection: TabConfigStructure[] = [];

    let objs = JSON.parse(JSON.stringify(object));
    for (let i = 0; i < objs.tabs.length; i++) {
      let o = objs.tabs[i];
      collection.push(new TabConfigStructure(o['titleKey'], this.mapJSONToDescriptorConfigStructure(o['config']), o['defaultTab']));
    }

    return collection;
  }

  private loadAppConfig(path: string) {
    if (!this.refreshConfig && localStorage.getItem('appConfig')) {
      this.appConfig = JSON.parse(localStorage.getItem('appConfig'));
      return Promise.resolve();
    } else {
      return this.http.get<AppConfig>(path)
        .toPromise()
        .then(data => {
          this.appConfig = data;
          localStorage.setItem('appConfig', JSON.stringify(this.appConfig));
        }).catch(reason => {
          console.log("Problem z przetwarzaniem pliku: " + path, reason);
        });
    }
  }

  private loadAnalyticsConfig(path: string) {
    if (!this.refreshConfig && localStorage.getItem('annalyticsConfig')) {
      this.annalyticsConfig = JSON.parse(localStorage.getItem('annalyticsConfig'));
      return Promise.resolve();
    } else {
      return this.http.get(path)
        .toPromise()
        .then(data => {
          this.annalyticsConfig = data;
          localStorage.setItem('annalyticsConfig', JSON.stringify(this.annalyticsConfig));
        }).catch(reason => {
          console.log("Problem z przetwarzaniem pliku: " + path, reason);
        });
    }
  }

  private loadIsbnDescriptionConfig(path: string) {
    if (!this.refreshConfig && localStorage.getItem('isbnDescriptionConfig')) {
      this.isbnDescriptionConfig = JSON.parse(localStorage.getItem('isbnDescriptionConfig'));
      return Promise.resolve();
    } else {
      return this.http.get(path)
        .toPromise()
        .then(data => {
          this.isbnDescriptionConfig = data;
          localStorage.setItem('isbnDescriptionConfig', JSON.stringify(this.isbnDescriptionConfig));
        }).catch(reason => {
          console.log("Problem z przetwarzaniem pliku: " + path, reason);
        });
    }
  }

  private loadArticlesFacetsConfig(path: string) {
    if (!this.refreshConfig && localStorage.getItem('workConfig_articlesConfig')) {
      this.workConfig.articlesConfig = JSON.parse(localStorage.getItem('workConfig_articlesConfig'));
      return Promise.resolve();
    } else {
      return this.http.get(path)
        .toPromise()
        .then(config => {
          const filtersConfig = config['filtersConfig'];
          if (filtersConfig && Array.isArray(filtersConfig)) {
            filtersConfig.forEach(filterConfig => {
              this.workConfig.articlesConfig.push(new FilterPortalConfig(filterConfig));
            });
            localStorage.setItem('workConfig_articlesConfig', JSON.stringify(this.workConfig.articlesConfig));
          }
        }).catch(reason => {
          console.log("Problem z przetwarzaniem pliku: " + path, reason);
        });
    }
  }

  private loadTopicsFacetsConfig(path: string) {
    if (!this.refreshConfig && localStorage.getItem('workConfig_topicsConfig')) {
      this.workConfig.topicsConfig = JSON.parse(localStorage.getItem('workConfig_topicsConfig'));
      return Promise.resolve();
    } else {
      return this.http.get(path)
        .toPromise()
        .then(config => {
          const filtersConfig = config['filtersConfig'];
          if (filtersConfig && Array.isArray(filtersConfig)) {
            filtersConfig.forEach(filterConfig => {
              this.workConfig.topicsConfig.push(new FilterPortalConfig(filterConfig));
            });
            localStorage.setItem('workConfig_topicsConfig', JSON.stringify(this.workConfig.topicsConfig));
          }
        }).catch(reason => {
          console.log("Problem z przetwarzaniem pliku: " + path, reason);
        });
    }
  }

  private loadDescriptorConfig(type: DescriptorEnum, path: string) {
    if (!this.refreshConfig && localStorage.getItem(`${type}Config`)) {
      this.descriptorConfig[`${type}Config`] = JSON.parse(localStorage.getItem(`${type}Config`));
      return Promise.resolve();
    } else {
      return this.http.get(path)
        .toPromise()
        .then((config) => {
            this.descriptorConfig.setDescriptorConfig(type, this.mapJSONToDescriptorConfigStructure(config));
            localStorage.setItem(`${type}Config`, JSON.stringify(this.descriptorConfig[`${type}Config`]));
          }
        ).catch(reason => {
          console.log("Problem z przetwarzaniem pliku: " + path, reason);
        });
    }
  }

  private loadWorkConfigFromFile(type, path: string) {
    if (!this.refreshConfig && localStorage.getItem('workConfig_bookConfig') && localStorage.getItem('workConfig_filtersConfig')) {
      this.workConfig.bookConfig = JSON.parse(localStorage.getItem('workConfig_bookConfig'));
      this.workConfig.filtersConfig = JSON.parse(localStorage.getItem('workConfig_filtersConfig'));
      return Promise.resolve();
    } else {
      return this.http.get(path)
        .toPromise()
        .then((config) => {
            this.workConfig.bookConfig = this.mapJSONToTabsConfigStructure(config);
            localStorage.setItem('workConfig_bookConfig', JSON.stringify(this.workConfig.bookConfig));
            const filtersConfig = config['filtersConfig'];
            if (filtersConfig && Array.isArray(filtersConfig)) {
              filtersConfig.forEach(filterConfig => {
                this.workConfig.filtersConfig.push(new FilterPortalConfig(filterConfig));
              });
            }
            localStorage.setItem('workConfig_filtersConfig', JSON.stringify(this.workConfig.filtersConfig));
          }
        ).catch(reason => {
          console.log("Problem z przetwarzaniem pliku: " + path, reason);
        });
    }
  }

  private loadPublicDatasetConfigFromFile(path: string) {
    return this.http.get(path)
      .toPromise()
      .then((config) => {
          this.publicDatasetConfig.publicDatasetConfig = this.mapJSONToTabsConfigStructure(config);
          const filtersConfig = config['filtersConfig'];
          if (filtersConfig && Array.isArray(filtersConfig)) {
            filtersConfig.forEach(filterConfig => {
              this.workConfig.filtersConfig.push(new FilterPortalConfig(filterConfig));
            });
          }
        }
      ).catch(reason => {
        console.log("Problem z przetwarzaniem pliku: " + path, reason);
      });
  }

  private loadMaterializationFromFile(type: MaterializationEnum, path: string) {
    if (!this.refreshConfig && localStorage.getItem('materializationConfig_bookConfig')) {
      this.materializationConfig.bookConfig = JSON.parse(localStorage.getItem('materializationConfig_bookConfig'));
      return Promise.resolve();
    } else {
      return this.http.get(path)
        .toPromise()
        .then((config) => {
            this.materializationConfig.bookConfig = this.mapJSONToTabsConfigStructure(config);
            localStorage.setItem('materializationConfig_bookConfig', JSON.stringify(this.materializationConfig.bookConfig));
          }
        ).catch(reason => {
          console.log("Problem z przetwarzaniem pliku: " + path, reason);
        });
    }
  }

  private loadWorkMaterializationFromFile(type: WorkMaterializationEnum, path: string) {
    if (!this.refreshConfig && localStorage.getItem('workMaterializationConfig_bookConfig')) {
      this.workMaterializationConfig.bookConfig = JSON.parse(localStorage.getItem('workMaterializationConfig_bookConfig'));
      return Promise.resolve();
    } else {
      return this.http.get(path)
        .toPromise()
        .then((config) => {
            this.workMaterializationConfig.bookConfig = this.mapJSONToTabsConfigStructure(config);
            localStorage.setItem('workMaterializationConfig_bookConfig', JSON.stringify(this.workMaterializationConfig.bookConfig));
          }
        ).catch(reason => {
          console.log("Problem z przetwarzaniem pliku: " + path, reason);
        });
    }
  }

  private loadFulltextFromFile(path: string) {
    if (!this.refreshConfig && localStorage.getItem('fulltextConfig_bookConfig')) {
      this.fulltextConfig.bookConfig = JSON.parse(localStorage.getItem('fulltextConfig_bookConfig'));
      return Promise.resolve();
    } else {
      return this.http.get(path)
        .toPromise()
        .then((config) => {
            this.fulltextConfig.bookConfig = this.mapJSONToDescriptorConfigStructure(config);
            localStorage.setItem('fulltextConfig_bookConfig', JSON.stringify(this.fulltextConfig.bookConfig));
          }
        ).catch(reason => {
          console.log("Problem z przetwarzaniem pliku: " + path, reason);
        });
    }
  }

  private loadSuggestionsConfig(path: string) {
    if (!this.refreshConfig && localStorage.getItem('suggestionsConfig')) {
      this.suggestionsConfig = JSON.parse(localStorage.getItem('suggestionsConfig'));
      return Promise.resolve();
    } else {
      return this.http.get(path)
        .toPromise()
        .then((rawSuggestionsConfig: any) => {
          this.suggestionsConfig = new SuggestionsConfig(rawSuggestionsConfig);
          localStorage.setItem('suggestionsConfig', JSON.stringify(this.suggestionsConfig));
        }).catch(reason => {
          console.log("Problem z przetwarzaniem pliku: " + path, reason);
        });
    }
  }

  private loadFiltersConfig(path: string) {
    if (!this.refreshConfig && localStorage.getItem('rawFiltersConfig') && localStorage.getItem('filtersConfig')) {
      this.rawFiltersConfig = JSON.parse(localStorage.getItem('rawFiltersConfig'));
      this.filtersConfig = JSON.parse(localStorage.getItem('filtersConfig'));
      return Promise.resolve();
    } else {
      return this.http.get(path)
        .toPromise()
        .then((rawFiltersConfig: any) => {
          this.rawFiltersConfig = rawFiltersConfig;
          localStorage.setItem('rawFiltersConfig', JSON.stringify(this.rawFiltersConfig));
          this.filtersConfig = new FiltersConfig(rawFiltersConfig);
          localStorage.setItem('filtersConfig', JSON.stringify(this.filtersConfig));
        }).catch(reason => {
          console.log("Problem z przetwarzaniem pliku: " + path, reason);
        });
    }
  }

  private loadWorkDetailsConfig(path: string) {
    if (!this.refreshConfig && localStorage.getItem('workDetailsConfig_workDetailsConfig')) {
      this.workDetailsConfig.workDetailsConfig = JSON.parse(localStorage.getItem('workDetailsConfig_workDetailsConfig'));
      return Promise.resolve();
    } else {
      return this.http.get(path)
        .toPromise()
        .then((config) => {
            this.workDetailsConfig.workDetailsConfig = this.mapJSONToDescriptorConfigStructure(config);
            localStorage.setItem('workDetailsConfig_workDetailsConfig', JSON.stringify(this.workDetailsConfig.workDetailsConfig));
          }
        ).catch(reason => {
          console.log("Problem z przetwarzaniem pliku: " + path, reason);
        });
    }
  }

  private loadMuseumObjectDetailsConfig(path: string) {
    if (!this.refreshConfig && localStorage.getItem('museumObjectDetailsConfig_museumObjectDetailsConfig')) {
      this.museumObjectDetailsConfig.museumObjectDetailsConfig = JSON.parse(localStorage.getItem('museumObjectDetailsConfig_museumObjectDetailsConfig'));
      return Promise.resolve();
    } else {
      return this.http.get(path)
        .toPromise()
        .then((config) => {
            this.museumObjectDetailsConfig.museumObjectDetailsConfig = this.mapJSONToDescriptorConfigStructure(config);
            localStorage.setItem('museumObjectDetailsConfig_museumObjectDetailsConfig', JSON.stringify(this.museumObjectDetailsConfig.museumObjectDetailsConfig));
          }
        ).catch(reason => {
          console.log("Problem z przetwarzaniem pliku: " + path, reason);
        });
    }
  }

  private loadMuseumObjectConfig(path: string) {
    if (!this.refreshConfig && localStorage.getItem('museumObjectConfig_museumObjectConfig')) {
      this.museumObjectConfig.museumObjectConfig = JSON.parse(localStorage.getItem('museumObjectConfig_museumObjectConfig'));
      return Promise.resolve();
    } else {
      return this.http.get(path)
        .toPromise()
        .then((config) => {
            this.museumObjectConfig.museumObjectConfig = this.mapJSONToDescriptorConfigStructure(config);
            localStorage.setItem('museumObjectConfig_museumObjectConfig', JSON.stringify(this.museumObjectConfig.museumObjectConfig));
          }
        ).catch(reason => {
          console.log("Problem z przetwarzaniem pliku: " + path, reason);
        });
    }
  }

  private loadLibraryPerPageConfig(path: string) {
    if (!this.refreshConfig && localStorage.getItem('librariesPerPage') && localStorage.getItem('institutionsLibrariesPerPage')) {
      this.librariesPerPage = JSON.parse(localStorage.getItem('librariesPerPage'));
      this.institutionsLibrariesPerPage = JSON.parse(localStorage.getItem('institutionsLibrariesPerPage'));
      return Promise.resolve();
    } else {
      return this.http.get(path)
        .toPromise()
        .then((config) => {
          this.librariesPerPage = config['count'];
          localStorage.setItem('librariesPerPage', JSON.stringify(this.librariesPerPage));
          this.institutionsLibrariesPerPage = config['institutionsCount'];
          localStorage.setItem('institutionsLibrariesPerPage', JSON.stringify(this.workConfig.topicsConfig));
        }).catch(reason => {
          console.log("Problem z przetwarzaniem pliku: " + path, reason);
        });
    }
  }

  private loadMaterializationListConfig(path: string) {
    if (!this.refreshConfig && localStorage.getItem('materializationListConfig')) {
      this.materializationListConfig = JSON.parse(localStorage.getItem('materializationListConfig'));
      return Promise.resolve();
    } else {
      return this.http.get(path)
        .toPromise()
        .then((config) => {
          this.materializationListConfig = this.mapJSONToDescriptorConfigStructure(config);
          localStorage.setItem('materializationListConfig', JSON.stringify(this.materializationListConfig));
        }).catch(reason => {
          console.log("Problem z przetwarzaniem pliku: " + path, reason);
        });
    }
  }

  private loadMaterializationPerPageConfig(path) {
    if (!this.refreshConfig && localStorage.getItem('materializationPerPage')) {
      this.materializationPerPage = JSON.parse(localStorage.getItem('materializationPerPage'));
      return Promise.resolve();
    } else {
      return this.http.get(path)
        .toPromise()
        .then((config) => {
          this.materializationPerPage = config['count'];
          localStorage.setItem('materializationPerPage', JSON.stringify(this.materializationPerPage));
        }).catch(reason => {
          console.log("Problem z przetwarzaniem pliku: " + path, reason);
        });
    }
  }

  private loadFieldsToSearchConfig(path: string) {
    if (!this.refreshConfig && localStorage.getItem('fieldsToSearch')) {
      this.fieldsToSearch = JSON.parse(localStorage.getItem('fieldsToSearch'));
      return Promise.resolve();
    } else {
      return this.http.get(path)
        .toPromise()
        .then((config) => {
          let configElement = config['fields'];
          if (configElement instanceof Array) {
            configElement.forEach(value => {
              this.fieldsToSearch.push(new FieldToSearch(value));
            });
            localStorage.setItem('fieldsToSearch', JSON.stringify(this.fieldsToSearch));
          } else {
            throw new Error("Empty fields");
          }
        }).catch(reason => {
          console.log("Problem z przetwarzaniem pliku: " + path, reason);
        });
    }
  }

  private loadZoomConfig(path: string) {
    if (!this.refreshConfig && localStorage.getItem('zoomConfig')) {
      this.zoomConfig = JSON.parse(localStorage.getItem('zoomConfig'));
      return Promise.resolve();
    } else {
      return this.http.get(path)
        .toPromise()
        .then((config) => {
          this.zoomConfig = config['zoom'];
          localStorage.setItem('zoomConfig', JSON.stringify(this.zoomConfig));
        }).catch(reason => {
          console.log("Problem z przetwarzaniem pliku: " + path, reason);
        });
    }
  }

  private loadBreadCrumbLengthConfig(path: string) {
    if (!this.refreshConfig && localStorage.getItem('breadCrumbLength')) {
      this.breadCrumbLength = JSON.parse(localStorage.getItem('breadCrumbLength'));
      return Promise.resolve();
    } else {
      return this.http.get(path)
        .toPromise()
        .then((config) => {
          this.breadCrumbLength = config['maxLength'];
          localStorage.setItem('breadCrumbLength', JSON.stringify(this.breadCrumbLength));
        }).catch(reason => {
          console.log("Problem z przetwarzaniem pliku: " + path, reason);
        });
    }
  }

  private loadElasticSearchConfig(path: string) {
    if (!this.refreshConfig && localStorage.getItem('maxResultWindow')) {
      this.maxResultWindow = JSON.parse(localStorage.getItem('maxResultWindow'));
      return Promise.resolve();
    } else {
      return this.http.get(path)
        .toPromise()
        .then((config) => {
          this.maxResultWindow = config['maxResultWindow'];
          localStorage.setItem('maxResultWindow', JSON.stringify(this.maxResultWindow));
        }).catch(reason => {
          console.log("Problem z przetwarzaniem pliku: " + path, reason);
        });
    }
  }

  private loadFacetTagMaxConfig(path: string) {
    if (!this.refreshConfig && localStorage.getItem('facetTagMax')) {
      this.facetTagMax = JSON.parse(localStorage.getItem('facetTagMax'));
      return Promise.resolve();
    } else {
      return this.http.get(path)
        .toPromise()
        .then((config) => {
          this.facetTagMax = config['max'];
          localStorage.setItem('facetTagMax', JSON.stringify(this.facetTagMax));
        }).catch(reason => {
          console.log("Problem z przetwarzaniem pliku: " + path, reason);
        });
    }
  }

  private loadTranslationConfig(path: string) {
    return this.http.get(path)
      .toPromise()
      .then((config) => {
        Object.keys(config).forEach(value => {
          let active = config[value]['active'];
          let def = config[value]['default'];
          this.languages.push({name: value, active: active});
          if (def) {
            this.htmlUtils.defaultLang.next(value);
          }
        })
      }).catch(reason => {
        console.log("Problem z przetwarzaniem pliku: " + path, reason);
      })
  }

  private loadShowMoreConfig(path: string) {
    if (!this.refreshConfig && localStorage.getItem('showMoreConfig')) {
      this.showMoreConfig = JSON.parse(localStorage.getItem('showMoreConfig'));
      return Promise.resolve();
    } else {
      return this.http.get(path)
        .toPromise()
        .then((config) => {
          this.showMoreConfig = {
            maxText: config['maxText'],
            maxListElements: config['maxListElements'],
            maxAuthors: config['maxAuthors']
          };
          localStorage.setItem('showMoreConfig', JSON.stringify(this.showMoreConfig));
        }).catch(reason => {
          console.log("Problem z przetwarzaniem pliku: " + path, reason);
        });
    }
  }

  private loadAccountConfig(path: string) {
    if (!this.refreshConfig && localStorage.getItem('accountConfig_timeout')) {
      this.accountConfig.timeout = JSON.parse(localStorage.getItem('accountConfig_timeout'));
      return Promise.resolve();
    } else {
      return this.http.get(path)
        .toPromise()
        .then((config) => {
          this.accountConfig.timeout = config['timeout'];
          localStorage.setItem('accountConfig_timeout', JSON.stringify(this.accountConfig.timeout));
        }).catch(reason => {
          console.log("Problem z przetwarzaniem pliku: " + path, reason);
        });
    }
  }

  private loadSiteMapConfig(path: string) {
    if (!this.refreshConfig && localStorage.getItem('siteMapConfig')) {
      this.siteMapConfig = JSON.parse(localStorage.getItem('siteMapConfig'));
      return Promise.resolve();
    } else {
      return this.http.get(path)
        .toPromise()
        .then((config) => {
          this.siteMapConfig = config;
          localStorage.setItem('siteMapConfig', JSON.stringify(this.siteMapConfig));
        }).catch(reason => {
          console.log("Problem z przetwarzaniem pliku: " + path, reason);
        });
    }
  }

  private loadDescriptorFacetConfig(path: string) {
    return this.http.get(path)
      .toPromise()
      .then((config: { config: any[] }) => {
        this.descriptorFacetConfig = new DescriptorsFacetConfig();
        let configMap = config['config'];
        if (configMap) {
          configMap.forEach(value => {
            const name = value['name'];
            const elementConfig = value['config'];
            const typesWithFilters = value['typesWithFilters'];
            if (elementConfig && typesWithFilters) {
              const facets = new FiltersConfig(elementConfig);
              const descriptor = new DescriptorFacetConfig({facets});
              Object.values(DescriptorEnum).map(type => descriptor.putFilterByType(type, typesWithFilters[type]));
              this.descriptorFacetConfig.putConfig(name, descriptor);
            } else {
              throw new Error(`Uzupełnij pola 'config' oraz 'typesWithFilters' dla konfiguracji o nazwie: '${name}'`);
            }
          });
        }
      }).catch(reason => {
        console.log("Problem z przetwarzaniem pliku: " + path, reason);
      });
  }

  public async loadEuConfig(path: string) {
    if (!this.refreshConfig && localStorage.getItem('euConfig')) {
      this.euConfig = JSON.parse(localStorage.getItem('euConfig'));
      return Promise.resolve();
    } else {
      try {
        const config = await this.http.get(path)
          .toPromise();
        this.euConfig.visible = config['visible'];
        this.euConfig.defaultLanguage = config['defaultLanguage'];
        this.euConfig.descriptionKey = config['descriptionKey'];
        this.euConfig.omnisLogo = config['omnisLogo'];
        this.euConfig.omnisLogoWcag = config['omnisLogoWcag'];
        this.euConfig.languages = config['languages'];
        localStorage.setItem('euConfig', JSON.stringify(this.euConfig));
      } catch (reason) {
        console.log("Problem z przetwarzaniem pliku: " + path, reason);
      }
    }
  }

  private loadMainPageConfig(path: string) {
    if (!this.refreshConfig && localStorage.getItem('mainPageConfig_messageKey') && localStorage.getItem('mainPageConfig_active')) {
      this.mainPageConfig.messageKey = JSON.parse(localStorage.getItem('mainPageConfig_messageKey'));
      this.mainPageConfig.active = JSON.parse(localStorage.getItem('mainPageConfig_active'));
      return Promise.resolve();
    } else {
      return this.http.get(path)
        .toPromise()
        .then((config) => {
          this.mainPageConfig.messageKey = config['messageKey'];
          localStorage.setItem('mainPageConfig_messageKey', JSON.stringify(this.mainPageConfig.messageKey));
          this.mainPageConfig.active = config['active'];
          localStorage.setItem('mainPageConfig_active', JSON.stringify(this.mainPageConfig.active));
        }).catch(reason => {
          console.log("Problem z przetwarzaniem pliku: " + path, reason);
        });
    }
  }

  // END CONFIGS ******

  private attachToCollection(object: any, collection: ConfigStructure[]) {
    let keys = Object.keys(object);
    if (keys.length === 0) {
      throw Error("Brak konfiguracji dla ");
    }
    keys.forEach(key => {

      let configStructure = this.createConfig(object, key);
      if (configStructure) {
        collection.push(configStructure);
      }
    });
  }

  private createConfig(object: any, key): ConfigStructure {
    let objectElement = object[key];
    return this.createSingleConfig(objectElement, key);
  }

  private createSingleConfig(objectElement, name: string) {
    let configStructure: ConfigStructure = null;
    if (Object.keys(objectElement).includes('template')) {
      configStructure = new ConfigStructure({
        show: objectElement.show,
        translation: objectElement.translation,
        name: name,
        template: objectElement.template,
        isLink: objectElement.isLink,
        allMatches: objectElement.allMatches,
        skipNewLine: objectElement.skipNewLine,
        list: objectElement.list
      });

      if (Object.keys(objectElement).includes('nestedConfig')) {
        let nestedConfigs: any[] = objectElement.nestedConfig;
        nestedConfigs.forEach((nestedConfig) => {
          let keys = Object.keys(nestedConfig);
          keys.forEach((key) => {
            configStructure.nestedConfig.push(this.createSingleConfig(nestedConfig[key], key));
          })
        });
      }

      if ((Object.keys(objectElement).includes('elseConfig'))) {
        let elseConf: any[] = objectElement.elseConfig;
        let keys = Object.keys(elseConf);
        keys.forEach((key) => {
          configStructure.elseConfig = this.createSingleConfig(elseConf[key], key);
        })

      }

    } else {
      throw new Error(`Obiekt ${name} nie posiada poprawnej konfiguracji `);
    }
    return configStructure;
  }

  public getIndexesStatus(exist: boolean) {
    return this.http.get<IndexDTO>(`apiUrl/omnis/index/all`, {params: {exist: exist ? 'true' : 'false'}})
  }
}

